import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import Seo from '../components/Seo';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import axios from 'axios';
import {
  MapPin, Ruler, Buildings, ArrowRight, CheckCircle,
  Crown, Rocket,
  Eye, EyeSlash, List, X, CaretDown, CaretLeft, CaretRight, Check, EnvelopeSimple,
  CraneTower, StackSimple, ChartLine, ArrowUp, House, ClockCounterClockwise, Star, ListChecks,
  CalendarBlank, ArrowsClockwise
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Alternating image/text rows shown in the new "See it in action" showcase section.
// Images live in /app/frontend/public/showcase/ — replace any file there to update.
// Exported so the alternative-layout preview routes (`/preview/landing-sticky`,
// `/preview/landing-tabs`) can render the same content in different layouts
// without duplicating the array.
export const SHOWCASE = [
  {
    icon: ChartLine,
    eyebrow: 'Analytics dashboard',
    title: 'See your team\u2019s output at a glance',
    desc: 'Track total measurements, area mapped and shape distribution across projects. Filter by team member, time range or shape type. Export the underlying data or share project-level PDF reports with clients in one click.',
    img: '/showcase/analytics.jpg',
    alt: 'Analytics dashboard showing 47 measurements across 8 projects, with charts and a shape-type donut',
  },
  {
    icon: Ruler,
    eyebrow: 'Map workspace',
    title: 'Draw, measure and label — directly on satellite imagery',
    desc: 'A full measurement workspace on top of live satellite imagery — twelve drawing tools, on-shape area & perimeter labels, a precise properties panel for every shape, and a Shapes drawer that lists everything you\u2019ve drawn at a glance. Built so estimators can quote a roof in minutes, not hours.',
    img: '/showcase/map-tools.jpg',
    alt: 'RoofMate map view of Herbert Street QLD with multiple measurements drawn over satellite imagery',
    // Three-slide carousel walking the user through the full workspace:
    // (1) the toolbar + on-shape labels, (2) the per-shape properties panel,
    // (3) the Shapes drawer that summarises every measurement on the project.
    slides: [
      {
        icon: Ruler,
        eyebrow: 'Map workspace',
        title: 'Draw, measure and label — directly on satellite imagery',
        desc: 'Rectangles, polygons, circles, multi-distance lines and shadow-height tools — all with live area & perimeter labels rendered directly on the shape. Switch between Satellite, Hybrid, Roadmap and Terrain with one click, and undo any move instantly.',
        img: '/showcase/map-tools.jpg',
        alt: 'RoofMate workspace showing a 763 m² rectangle, a 318.7 m² circle, a 1090.2 m² polygon and a multi-distance line, all measured on satellite imagery of Herbert Street QLD',
      },
      {
        icon: Ruler,
        eyebrow: 'Shape properties',
        title: 'Edit length, width and rotation with surgical precision',
        desc: 'Click any shape to open its live Properties panel — Area, Perimeter, Rotation, Length and Width are all editable. Rectangles keep their orthogonality across edits, rotation is undoable, and every value persists to the project the moment you tab out.',
        img: '/showcase/shape-properties.jpg',
        alt: 'Shape properties panel for Rectangle 3 showing area 763 m², perimeter 123 m, rotation 161°, length 54.5 m, width 14 m',
      },
      {
        icon: ListChecks,
        eyebrow: 'Saved shapes at a glance',
        title: 'Every measurement in one tidy drawer',
        desc: 'The Shapes drawer groups Measurements and Site Logistics on their own tabs. Each row carries the shape\u2019s colour, type, key dimensions and per-row Show / Edit / Note / Copy / Lock / Delete actions — so quotes, audits and handovers stay fast even on busy projects.',
        img: '/showcase/saved-shapes.jpg',
        alt: 'Saved Shapes drawer listing Circle 2, Polygon 3, Rectangle 3, Marker 4 and Multi-Dist 5 with their measurements and per-row action icons',
      },
    ],
  },
  {
    icon: StackSimple,
    eyebrow: 'Australian cadastre overlay',
    title: 'Property boundaries from every Australian state — built in',
    desc: 'Toggle official lot boundaries from NSW, VIC, QLD, WA, SA, TAS, NT and ACT spatial services. Outlines stay crisp at every zoom level thanks to on-canvas edge processing, with an adjustable opacity wash for context.',
    img: '/showcase/cadastre.jpg',
    alt: 'Cadastre property boundary lines overlaid on a Queensland satellite map',
    // Two-slide carousel: cadastre overlay (above) + parcel inspect (below).
    // The outer `icon/eyebrow/title/desc/img/alt` are used for slide 0; slide 1
    // overrides them via `slides[1]`. Renderer keys off `slides?.length`.
    slides: [
      {
        icon: StackSimple,
        eyebrow: 'Australian cadastre overlay',
        title: 'Property boundaries from every Australian state — built in',
        desc: 'Toggle official lot boundaries from NSW, VIC, QLD, WA, SA, TAS, NT and ACT spatial services. Outlines stay crisp at every zoom level thanks to on-canvas edge processing, with an adjustable opacity wash for context.',
        img: '/showcase/cadastre.jpg',
        alt: 'Cadastre property boundary lines overlaid on a Queensland satellite map',
      },
      {
        icon: House,
        eyebrow: 'Parcel inspect',
        title: 'Click a lot — get the address, area, lot, plan & status instantly',
        desc: 'At street-level zoom, every cadastre parcel becomes interactive. Click any lot to lock a yellow highlight on it and surface a side panel with the reverse-geocoded street address, lat/lng, parcel area (m²), parcel status (Freehold / Reserve / Crown), parcel type, Plan & Lot numbers, locality and state. One tap to drop a saved Marker on the parcel or to copy a shareable Google Maps link — perfect for pre-site briefings and quoting.',
        img: '/showcase/parcel-info.jpg',
        alt: 'Cadastre parcel selected with yellow highlight and a Property Info panel showing address, coordinates, parcel area, status and Plan/Lot numbers',
      },
    ],
  },
  {
    icon: CraneTower,
    eyebrow: 'Site planning',
    title: 'Plan crane lifts, offices, storage and amenities pre-arrival',
    desc: 'Drop Crane, Site Office, Storage, Toilet and Skip Bin markers. Built for pre-job toolbox talks and traffic management plans. The Site Logistics tab keeps planning data separate from measurements.',
    img: '/showcase/site-logistics.jpg',
    alt: 'Site logistics markers (crane with radius, office, storage) on a London project',
    // Two-slide carousel: the Site Plan toolbar popover (the catalog of
    // available markers) followed by a populated map showing the same
    // markers actually placed on a London project — "before/after planning".
    slides: [
      {
        icon: CraneTower,
        eyebrow: 'Site planning — toolbar',
        title: 'One toolbar, every site-planning marker you need',
        desc: 'Open the Site Plan popover to see the full catalog at a glance: Crane, Site Office, Storage, Toilet and Skip Bin. Single-click placement, draggable thereafter — same UX as the standard marker tool.',
        img: '/showcase/site-logistics.jpg',
        alt: 'RoofMate Site Logistics toolbar popover open over a satellite map, showing chips for Crane, Site Office, Storage, Toilet and Skip Bin',
      },
      {
        icon: CraneTower,
        eyebrow: 'Site planning — placed on the map',
        title: 'Plan the whole site before a boot hits the property',
        desc: 'Two cranes, a Site Office, Storage, Toilet and Skip Bin — all colour-coded, all draggable, all persisting per project. Site logistics live on their own tab so they never clutter your roof measurements.',
        img: '/showcase/site-logistics-placed.jpg',
        alt: 'A populated London Roof Survey project with two crane markers, a Site Office, Storage, Toilet and Skip Bin marker placed across the satellite view',
      },
    ],
  },
  {
    icon: CalendarBlank,
    eyebrow: 'Historical satellite imagery — Pro & Enterprise',
    title: 'Pick a satellite date and lock it to the project',
    desc: 'Inside the map workspace, hit the new Date ▾ pill next to the Satellite / Hybrid / Roadmap buttons and pick any of ~194 Esri Wayback snapshots from 2014 to today. The base imagery swaps in place, the choice is saved per project, and your drawn shapes, cadastre overlay and measurements all stay rendered on top. Open the same project tomorrow and the imagery is exactly where you left it. Available on Pro and Enterprise plans.',
    img: '/showcase/time-travel-timeline.jpg',
    alt: 'RoofMate Imagery Date pill in the map workspace, showing a dropdown of Esri Wayback release dates with one selected and the satellite imagery swapped to match',
  },
  {
    icon: ClockCounterClockwise,
    eyebrow: 'Time Travel',
    title: 'Compare the property today vs years ago — with Swipe & Timeline',
    desc: 'Toggle between Swipe (drag a vertical divider across two dates) and Timeline (scrub through every Wayback snapshot between the chosen dates) with one click. A floating date bubble follows the slider, ◀ / ▶ steppers jump between releases, and the ▶ Play button auto-advances the time-lapse. Sync snaps both views back to the property. Powered by 193 Esri World Imagery Wayback snapshots from 2014 to present — perfect for storm-damage claims, before/after reporting and council submissions.',
    img: '/showcase/time-travel-swipe.jpg',
    alt: 'RoofMate Time Travel Swipe mode showing a vertical divider between Left date 03/05/2023 (vegetation, vacant lot) and Right date 30/04/2026 (built warehouses) over Herbert Street QLD',
    demo: 'time-travel',  // enables the "▶ See it in action" CTA chip on this row only
    // Four-slide carousel that walks the user through the entire Time-Travel
    // workflow: (1) Swipe before/after, (2) Timeline scrubber, (3) Export
    // formats popover (MP4 / GIF / Copy link / Send to client) and (4) the
    // Recipients dialog showing engagement analytics back from the share
    // links. Sequenced this way the user reads it as a story: compare → animate
    // → export → measure outreach.
    slides: [
      {
        icon: ClockCounterClockwise,
        eyebrow: 'Swipe mode',
        title: 'Drag a vertical divider between two dates — see the change instantly',
        desc: 'Pick any two Wayback dates and Swipe drops a vertical divider across the property. Drag it left or right to reveal the older or newer imagery — perfect for storm-damage claims, before/after reports and council submissions.',
        img: '/showcase/time-travel-swipe.jpg',
        alt: 'RoofMate Time Travel Swipe mode showing a vertical divider between Left date 03/05/2023 and Right date 30/04/2026 with a draggable handle in the middle',
      },
      {
        icon: ClockCounterClockwise,
        eyebrow: 'Timeline mode',
        title: 'Scrub through every Wayback snapshot between the chosen dates',
        desc: 'Switch to Timeline and a horizontal scrubber lists every Esri Wayback release between your Left and Right dates. A floating date bubble follows the thumb, ◀ / ▶ steppers jump between releases, and ▶ Play auto-advances the time-lapse — turning a years-long history into a 10-second animation.',
        img: '/showcase/time-travel-timeline.jpg',
        alt: 'RoofMate Time Travel Timeline mode with a scrubber from 03/05/2023 to 30/04/2026, a floating 18/11/2024 date bubble and a ▶ Play / Export control bar',
      },
      {
        icon: ClockCounterClockwise,
        eyebrow: 'Export & share',
        title: 'Export an MP4 or GIF — or send a branded link to the client',
        desc: 'One Export menu, four ways to share: download an MP4 video or GIF for proposal decks, copy a 7-day public link straight to clipboard, or hit “Send to client” for a branded email with the time-lapse embedded. Every link is rate-limited and access-logged for compliance.',
        img: '/showcase/time-travel-export.jpg',
        alt: 'RoofMate Time Travel Export popover open in the bottom-right with options MP4 (video), GIF (image), Copy share link (7-day) and Send to client (branded email)',
      },
      {
        icon: ClockCounterClockwise,
        eyebrow: 'Recipients & analytics',
        title: 'See exactly who opened your time-lapse — and from where',
        desc: 'A Time-lapse Recipients dialog tracks every share you’ve created for the project: per-recipient open counts, last-opened timestamp, IP address, approximate geolocation and browser. Tenant admins can nudge unopened recipients with one click — closing the loop between estimator and homeowner.',
        img: '/showcase/time-travel-recipients.jpg',
        alt: 'RoofMate Time-lapse Recipients dialog showing share stats (3 shares, 7 clicks, 3 live) and per-share rows with IP address, city and browser of the last open',
      },
    ],
  },
  {
    icon: ArrowsClockwise,
    eyebrow: 'View modes — free for everyone',
    title: '2D, 45° bird\u2019s-eye and 360° Street View — switch in one click',
    desc: 'Right next to the SATELLITE / HYBRID / ROADMAP buttons you\u2019ll find a new 2D / 45° / 360° pill. 2D locks the map top-down for precise drawing; 45° tilts to Google\u2019s bird\u2019s-eye oblique imagery — best for assessing roof pitch, chimneys and dormers; 360° opens a full Street View panorama at the property so you can inspect driveways, front-of-house access and neighbouring buildings before you quote. All three modes work in both the full Project workspace and the Quick Look address inspector. No upgrade required — free on every plan.',
    img: '/showcase/map-tools.jpg',
    alt: 'RoofMate map workspace with a 2D / 45° / 360° pill highlighted next to the SATELLITE / HYBRID / ROADMAP / TERRAIN switcher',
  },
];

const STATS = [
  { value: '99.9%', label: 'Uptime' },
  { value: '50K+', label: 'Measurements' },
  { value: '500+', label: 'Projects' },
  { value: '24/7', label: 'Support' },
];

const PLAN_ICONS = { basic: Buildings, pro: Rocket, enterprise: Crown };
const PLAN_COLORS = { basic: '#7367F0', pro: '#FF9F43', enterprise: '#28C76F' };

// Renders a single cell of the feature comparison table.
// - true  → green check icon
// - false → muted dash
// - string → italic value (e.g. "Unlimited")
function renderCell(v) {
  if (v === true) return <Check size={16} weight="bold" className="inline text-success" data-testid="compare-cell-yes" />;
  if (v === false) return <span className="opacity-30 text-base" data-testid="compare-cell-no">—</span>;
  return <span className="text-[12px] opacity-80 italic" data-testid="compare-cell-value">{v}</span>;
}

// Media-only render of a SHOWCASE entry: image + optional carousel
// controls + optional "See it in action" demo CTA. No eyebrow / title /
// description text — those live in the left-column text blocks of the
// sticky-scroll layout. Used by the Features section to fill the
// right-hand sticky viewport (and by the mobile fallback inline above
// each text block). Carousel state is owned per-row so each
// independently-scrubbed carousel keeps its current slide on
// re-entry.
function SlideMedia({ s, i, onEnlarge, onDemoOpen, eager = false }) {
  const hasSlides = Array.isArray(s.slides) && s.slides.length > 1;
  const [active, setActive] = useState(0);
  // Preload every slide image once on mount so subsequent prev/next clicks
  // swap the <img src> against an already-cached blob — image flips in the
  // same frame as the text instead of arriving 200-400ms late on slower
  // connections. No-op for non-carousel rows.
  useEffect(() => {
    if (!hasSlides) return;
    s.slides.forEach((slide) => {
      if (!slide?.img) return;
      const pre = new Image();
      pre.src = slide.img;
    });
  }, [hasSlides, s.slides]);
  const view = hasSlides ? s.slides[active] : s;
  const goPrev = () => setActive((a) => (a === 0 ? s.slides.length - 1 : a - 1));
  const goNext = () => setActive((a) => (a + 1) % s.slides.length);
  return (
    <div className="relative w-full h-full group" data-testid={`showcase-media-${i}`}>
      <button
        type="button"
        onClick={() => onEnlarge({ src: view.img, alt: view.alt })}
        aria-label={`Enlarge: ${view.alt}`}
        data-testid={`showcase-img-btn-${i}`}
        className="block w-full h-full text-left cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <img
          src={view.img}
          alt={view.alt}
          loading={eager ? 'eager' : 'lazy'}
          width="1920"
          height="1080"
          className="w-full h-full object-contain block bg-card"
          data-testid={`showcase-img-${i}`}
        />
        <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur text-white text-[11px] font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200" aria-hidden="true">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3M11 8v6M8 11h6" /></svg>
          Click to enlarge
        </span>
      </button>
      {/* Per-slide caret buttons + dots (only when row has multiple slides). */}
      {hasSlides && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            aria-label="Previous slide"
            data-testid={`showcase-prev-${i}`}
            className="absolute left-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-10 h-10 rounded-full bg-black/55 backdrop-blur text-white hover:bg-black/75 hover:scale-105 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            <CaretLeft size={18} weight="bold" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            aria-label="Next slide"
            data-testid={`showcase-next-${i}`}
            className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-10 h-10 rounded-full bg-black/55 backdrop-blur text-white hover:bg-black/75 hover:scale-105 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            <CaretRight size={18} weight="bold" />
          </button>
          <div
            className="absolute bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-black/55 backdrop-blur"
            data-testid={`showcase-dots-${i}`}
          >
            {s.slides.map((slide, sIdx) => (
              <button
                type="button"
                key={slide.eyebrow || sIdx}
                onClick={(e) => { e.stopPropagation(); setActive(sIdx); }}
                aria-label={`Go to slide ${sIdx + 1}: ${slide.eyebrow}`}
                data-testid={`showcase-dot-${i}-${sIdx}`}
                aria-current={sIdx === active ? 'true' : undefined}
                className={`h-1.5 rounded-full transition-all ${sIdx === active ? 'w-6 bg-white' : 'w-1.5 bg-white/50 hover:bg-white/80'}`}
              />
            ))}
          </div>
        </>
      )}
      {/* "See it in action" CTA chip — only on rows that opt in. */}
      {s.demo === 'time-travel' && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDemoOpen(); }}
          className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-primary text-primary-foreground shadow-lg hover:opacity-95 hover:scale-[1.03] transition-all text-xs sm:text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          data-testid="time-travel-demo-cta"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
          See it in action
        </button>
      )}
    </div>
  );
}


const LANDING_FAQS = [
  {
    q: 'What is RoofMate?',
    a: 'RoofMate is roof measurement software that lets roofers and builders measure roofs from high-resolution satellite imagery, plan site logistics (crane, site office, storage, toilets, skip bins) and collaborate on projects with their team.',
  },
  {
    q: 'Is there a free trial?',
    a: 'Yes — every new RoofMate account starts with a 7-day free trial of any plan. You can cancel anytime from the Billing page.',
  },
  {
    q: 'How accurate are the satellite roof measurements?',
    a: 'RoofMate uses Google Maps high-resolution imagery. Area, perimeter and distance are calculated using the geometry library and are accurate to within a few centimetres at building scale. For pitched roofs, apply a pitch correction factor for true surface area.',
  },
  {
    q: 'Can my team work on projects together?',
    a: 'Yes. Each RoofMate organisation can invite team members, share projects, assign roles, and view a shared audit log. Per-user pricing applies on Pro and Enterprise plans.',
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [showSignup, setShowSignup] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('basic');
  const [form, setForm] = useState({ organization: '', name: '', email: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [planMenuOpen, setPlanMenuOpen] = useState(false);

  // Contact form
  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState('');
  const [contactSuccess, setContactSuccess] = useState(false);

  // Showcase lightbox — clicking a feature screenshot enlarges it.
  const [lightboxImg, setLightboxImg] = useState(null);
  const [demoOpen, setDemoOpen] = useState(false);
  useEffect(() => {
    if (!lightboxImg) return;
    const onKey = (e) => { if (e.key === 'Escape') setLightboxImg(null); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [lightboxImg]);

  // Show "scroll to top" floating button once the user scrolls past the hero section.
  const [showTopBtn, setShowTopBtn] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowTopBtn(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Sticky-scroll Features section: track which left-column text block is
  // closest to viewport centre, then crossfade the matching image in the
  // sticky right-column viewport. Driven by IntersectionObserver with a
  // 40%/40% rootMargin band so a block becomes "active" only once it's
  // crossed the centre line, not the very moment it enters the screen.
  const [featuresActiveIdx, setFeaturesActiveIdx] = useState(0);
  const featureBlockRefs = useRef([]);
  useEffect(() => {
    const blocks = featureBlockRefs.current.filter(Boolean);
    if (blocks.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          const idx = Number(visible[0].target.dataset.idx);
          if (!Number.isNaN(idx)) setFeaturesActiveIdx(idx);
        }
      },
      { rootMargin: '-40% 0px -40% 0px', threshold: [0.1, 0.5, 1] }
    );
    blocks.forEach((b) => observer.observe(b));
    return () => observer.disconnect();
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  useEffect(() => {
    // Plans are a soft-fail enhancement: landing page renders without them if the public endpoint is briefly unavailable.
    axios.get(`${API}/billing/plans`).then(res => setPlans(res.data || [])).catch(() => {});
  }, []);

  // Live "social proof" count for the Features trust strip. The server
  // floors the display string at "50+" (and rounds down to the nearest
  // 10 above that), so we never show an embarrassingly small number
  // while still auto-growing as RoofMate scales. The hardcoded initial
  // value matches the server floor so the strip never flashes a
  // different number on first paint.
  const [socialProofDisplay, setSocialProofDisplay] = useState('50+');
  useEffect(() => {
    axios.get(`${API}/marketing/customer-count`)
      .then((res) => {
        const d = res?.data?.display;
        if (typeof d === 'string' && d) setSocialProofDisplay(d);
      })
      .catch(() => { /* keep the "50+" fallback */ });
  }, []);

  function openSignup(planId) {
    setSelectedPlan(planId || 'basic');
    setForm({ organization: '', name: '', email: '', password: '' });
    setError(''); setSuccess(false);
    setPlanMenuOpen(false);
    setShowSignup(true);
  }

  async function handleSignup(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/signup`, {
        ...form,
        plan_id: selectedPlan,
        origin_url: window.location.origin,
      });
      const data = res.data || {};
      if (data.requires_payment && data.checkout_url) {
        // Redirect to Stripe Checkout (card collection for 7-day trial)
        window.location.href = data.checkout_url;
        return;
      }
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setLoading(false); }
  }

  const activePlans = plans.filter(p => p.is_active !== false);

  // Structured data for the public landing page.
  // - SoftwareApplication with live price offers in AUD
  // - FAQPage so common questions get rich-snippet treatment
  // - WebSite with SearchAction (for sitelinks searchbox eligibility)
  const seoJsonLd = useMemo(() => {
    const offers = activePlans.length > 0
      ? activePlans.map(p => ({
          '@type': 'Offer',
          name: p.name,
          price: String(p.price ?? 0),
          priceCurrency: 'AUD',
          url: 'https://roofmate.app/#pricing',
          availability: 'https://schema.org/InStock',
          category: 'subscription',
        }))
      : undefined;
    const ld = [
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'RoofMate',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        url: 'https://roofmate.app/',
        description: 'Roof measurement software for Australian roofers and builders. Measure roofs from high-resolution satellite imagery, plan site logistics, and share reports.',
        ...(offers ? { offers } : {}),
        publisher: { '@type': 'Organization', name: 'RoofMate', url: 'https://roofmate.app/' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        url: 'https://roofmate.app/',
        name: 'RoofMate',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://roofmate.app/?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: LANDING_FAQS.map(item => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ];
    return ld;
  }, [activePlans]);

  async function handleContactSubmit(e) {
    e.preventDefault();
    setContactError('');
    setContactSuccess(false);
    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) {
      setContactError('Please fill in name, email, and message.');
      return;
    }
    if (contactForm.message.trim().length < 10) {
      setContactError('Please share a bit more detail (minimum 10 characters).');
      return;
    }
    setContactLoading(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      setContactSuccess(true);
      setContactForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setContactError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setContactLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <Seo
        title="Roof Measurement Software for Australian Roofers"
        description="RoofMate is roof measurement software for Australian roofers and builders. Measure roofs from satellite imagery, plan site logistics, share reports — free 7-day trial."
        path="/"
        keywords="roof measurement software, satellite roof measurement, aerial roof measurement, roofing software australia, roof area calculator, site logistics planning"
        jsonLd={seoJsonLd}
      />
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-card/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <div className="flex items-center cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img src="/logo-full.png" alt="RoofMate logo" className="h-8 sm:h-[2.4rem] object-contain" width="180" height="40" />
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-muted-foreground">
            <button onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-foreground transition-colors">Features</button>
            <button onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-foreground transition-colors">Pricing</button>
            <button onClick={() => document.getElementById('compare')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-foreground transition-colors" data-testid="nav-compare-btn">Compare</button>
            <button onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-foreground transition-colors">How It Works</button>
            <a href="/blog" className="hover:text-foreground transition-colors" data-testid="nav-blog-link">Blog</a>
            <button onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-foreground transition-colors" data-testid="nav-contact-btn">Contact</button>
          </div>
          <div className="hidden sm:flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate('/login')} className="rounded-md font-medium text-sm h-9 px-5" data-testid="nav-login-btn">Sign In</Button>
            <Button onClick={() => openSignup('basic')} className="rounded-md font-medium text-sm h-9 px-5" data-testid="nav-signup-btn">Get Started <ArrowRight size={14} className="ml-1" weight="bold" /></Button>
          </div>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="sm:hidden w-9 h-9 flex items-center justify-center rounded-md hover:bg-secondary">
            {mobileMenuOpen ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="sm:hidden border-t border-border bg-card px-4 py-3 space-y-2">
            <button onClick={() => { document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' }); setMobileMenuOpen(false); }} className="block w-full text-left py-2 text-sm font-medium text-muted-foreground hover:text-foreground">Features</button>
            <button onClick={() => { document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); setMobileMenuOpen(false); }} className="block w-full text-left py-2 text-sm font-medium text-muted-foreground hover:text-foreground">Pricing</button>
            <button onClick={() => { document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' }); setMobileMenuOpen(false); }} className="block w-full text-left py-2 text-sm font-medium text-muted-foreground hover:text-foreground">How It Works</button>
            <a href="/blog" className="block py-2 text-sm font-medium text-muted-foreground hover:text-foreground">Blog</a>
            <button onClick={() => { document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); setMobileMenuOpen(false); }} className="block w-full text-left py-2 text-sm font-medium text-muted-foreground hover:text-foreground">Contact</button>
            <div className="flex gap-2 pt-2 border-t border-border">
              <Button variant="outline" onClick={() => navigate('/login')} className="flex-1 rounded-md text-sm h-9">Sign In</Button>
              <Button onClick={() => openSignup('basic')} className="flex-1 rounded-md text-sm h-9">Get Started</Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeIn">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-xs font-semibold mb-6">
                <Rocket size={14} weight="bold" /> Professional Roofing Platform
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold leading-[1.15] tracking-tight mb-4 sm:mb-5">
                Measure Roofs with<span className="text-primary"> Satellite Precision</span>
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6 sm:mb-7 max-w-lg">
                Access live satellite imagery, draw shapes to measure roofs and buildings, manage projects across your organization — all in one powerful platform.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Button onClick={() => openSignup('basic')} className="rounded-md font-medium text-sm h-10 px-6 shadow-lg shadow-primary/25" data-testid="hero-signup-btn">
                  Get Started <ArrowRight size={16} className="ml-1.5" weight="bold" />
                </Button>
                <Button onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })} variant="outline" className="rounded-md font-medium text-sm h-10 px-6">
                  View Pricing
                </Button>
              </div>
              <div className="flex items-center gap-3 sm:gap-5 mt-6 sm:mt-8 text-[11px] sm:text-xs text-muted-foreground flex-wrap" data-testid="hero-trial-badges">
                <span className="flex items-center gap-1.5"><CheckCircle size={16} className="text-success" weight="fill" /> {activePlans.length > 0 ? `From $${Math.min(...activePlans.map(p => p.price)).toFixed(0)}/user/mo` : 'Affordable plans'}</span>
                <span className="flex items-center gap-1.5"><CheckCircle size={16} className="text-success" weight="fill" /> 7-day free trial</span>
                <span className="flex items-center gap-1.5"><CheckCircle size={16} className="text-success" weight="fill" /> Cancel anytime</span>
              </div>
            </div>
            <div className="relative animate-fadeIn" style={{ animationDelay: '0.15s' }}>
              <div className="rounded-xl overflow-hidden shadow-2xl shadow-primary/10 border border-border">
                <img
                  src="https://images.unsplash.com/photo-1547717826-01f4fb5496ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBob3VzZSUyMGFlcmlhbCUyMHJvb2Z8ZW58MHx8fHwxNzc2MjMyODcxfDA&ixlib=rb-4.1.0&q=85"
                  alt="Aerial satellite view of a modern house roof — RoofMate satellite roof measurement"
                  className="w-full aspect-[4/3] object-cover"
                  loading="eager"
                  fetchpriority="high"
                  width="1200"
                  height="900"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-card shadow-card rounded-lg p-4 border border-border hidden lg:block">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-success/10 flex items-center justify-center"><Ruler size={20} className="text-success" weight="duotone" /></div>
                  <div><p className="text-sm font-semibold">Auto Measurement</p><p className="text-xs text-muted-foreground">Area, distance & perimeter</p></div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-card shadow-card rounded-lg p-4 border border-border hidden lg:block">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center"><MapPin size={20} className="text-primary" weight="duotone" /></div>
                  <div><p className="text-sm font-semibold">Google Satellite</p><p className="text-xs text-muted-foreground">High-res imagery</p></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section id="stats" className="border-y border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((s) => <div key={s.label} className="text-center"><p className="text-2xl sm:text-3xl font-bold text-primary">{s.value}</p><p className="text-xs text-muted-foreground mt-1">{s.label}</p></div>)}
          </div>
        </div>
      </section>

      {/* Features — visual showcase (alternating image/text rows) */}
      <section id="features" className="py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-xs font-semibold mb-4">Features</span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">Everything you need to measure, plan and quote</h2>
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">From satellite imagery to live cadastre overlays, RoofMate gives you the tools to measure, plan and report — without leaving the browser.</p>
            {/* Social proof strip — overlapping initials avatars, 5-star
                rating, and a state-coverage byline. Sits inline under the
                section header so visitors get a credibility beat before
                scrolling into the feature reel. */}
            <div
              className="mt-6 inline-flex items-center gap-3 sm:gap-4 px-4 py-2 rounded-full border border-border bg-card/60 backdrop-blur"
              data-testid="features-social-proof"
            >
              {/* Overlapping initials avatars. Three colour-coded circles —
                  the colours map to the three eastern AU states (NSW, VIC,
                  QLD) the byline calls out, which makes the cluster read as
                  "geographically diverse" at a glance. */}
              <div className="flex -space-x-2" aria-hidden="true">
                <span className="w-7 h-7 rounded-full ring-2 ring-card bg-gradient-to-br from-blue-500 to-blue-700 text-white text-[10px] font-bold flex items-center justify-center" title="NSW roofer">JM</span>
                <span className="w-7 h-7 rounded-full ring-2 ring-card bg-gradient-to-br from-amber-500 to-orange-600 text-white text-[10px] font-bold flex items-center justify-center" title="VIC roofer">AK</span>
                <span className="w-7 h-7 rounded-full ring-2 ring-card bg-gradient-to-br from-emerald-500 to-emerald-700 text-white text-[10px] font-bold flex items-center justify-center" title="QLD roofer">DR</span>
                <span
                  className="px-2 h-7 rounded-full ring-2 ring-card bg-secondary text-muted-foreground text-[10px] font-bold flex items-center justify-center"
                  title={`Trusted by ${socialProofDisplay} roofing teams`}
                  data-testid="social-proof-count-chip"
                >{socialProofDisplay}</span>
              </div>
              <div className="hidden sm:block h-6 w-px bg-border" aria-hidden="true" />
              {/* 5-star rating + byline. Stars are amber-500 to read as
                  "rating", not "warning". Byline copy stays short on mobile
                  by hiding the parenthetical count below sm. */}
              <div className="flex flex-col items-start gap-0.5">
                <div className="flex items-center gap-0.5" aria-label="Rated 5 out of 5 stars by customers">
                  {[0, 1, 2, 3, 4].map((idx) => (
                    <Star key={idx} size={12} weight="fill" className="text-amber-500" />
                  ))}
                </div>
                <p className="text-[11px] sm:text-xs text-muted-foreground leading-tight" data-testid="social-proof-byline">
                  Trusted by <span className="font-semibold text-foreground">{socialProofDisplay} roofing teams</span> across <span className="font-semibold text-foreground">NSW</span>, <span className="font-semibold text-foreground">VIC</span> &amp; <span className="font-semibold text-foreground">QLD</span>
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="features-sticky-grid">
            {/* Left column: scrolling text blocks (always rendered). */}
            <div className="lg:col-span-5 space-y-24 lg:space-y-40" data-testid="features-text-column">
              {SHOWCASE.map((s, i) => {
                const Icon = s.icon;
                const isActive = featuresActiveIdx === i;
                return (
                  <div
                    key={s.title}
                    ref={(el) => { featureBlockRefs.current[i] = el; }}
                    data-idx={i}
                    data-testid={`showcase-row-${i}`}
                    className={`transition-opacity duration-500 ${isActive ? 'opacity-100' : 'opacity-50 lg:hover:opacity-100'}`}
                  >
                    <div
                      className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-semibold tracking-wide mb-4 uppercase"
                      data-testid={`showcase-eyebrow-${i}`}
                    >
                      <Icon size={14} weight="duotone" /> {s.eyebrow}
                    </div>
                    <h3
                      className="text-2xl sm:text-3xl font-bold tracking-tight mb-4 leading-tight"
                      data-testid={`showcase-title-${i}`}
                    >
                      {s.title}
                    </h3>
                    <p
                      className="text-sm sm:text-base text-muted-foreground leading-relaxed"
                      data-testid={`showcase-desc-${i}`}
                    >
                      {s.desc}
                    </p>
                    {/* Inline image fallback on mobile/tablet (the sticky right
                        column is hidden below lg). The full SlideMedia is
                        reused so internal carousels + the Time Travel CTA
                        still work on small screens. */}
                    <div className="mt-6 lg:hidden rounded-xl overflow-hidden border border-border shadow-lg aspect-[16/10] bg-secondary/30">
                      <SlideMedia
                        s={s}
                        i={i}
                        onEnlarge={setLightboxImg}
                        onDemoOpen={() => setDemoOpen(true)}
                        eager={i === 0}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Right column: sticky viewport that crossfades between images as
                the matching text block on the left passes the viewport centre.
                Hidden below lg where the inline mobile images take over. */}
            <div className="hidden lg:block lg:col-span-7">
              <div className="sticky top-24" data-testid="features-sticky-viewport">
                <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-border shadow-2xl bg-card">
                  {SHOWCASE.map((s, i) => (
                    <div
                      key={s.title}
                      className={`absolute inset-0 transition-opacity duration-700 ease-out ${featuresActiveIdx === i ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                      aria-hidden={featuresActiveIdx !== i}
                    >
                      <SlideMedia
                        s={s}
                        i={i}
                        onEnlarge={setLightboxImg}
                        onDemoOpen={() => setDemoOpen(true)}
                        eager={i === 0}
                      />
                    </div>
                  ))}
                </div>
                {/* Clickable progress strip — jump to a feature by clicking. */}
                <div className="mt-5 flex items-center justify-center gap-2" data-testid="features-sticky-progress">
                  {SHOWCASE.map((s, i) => (
                    <button
                      key={s.title}
                      type="button"
                      onClick={() => {
                        featureBlockRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                      aria-label={`Jump to ${s.eyebrow}`}
                      aria-current={featuresActiveIdx === i ? 'true' : undefined}
                      data-testid={`features-jump-${i}`}
                      className={`h-1.5 rounded-full transition-all ${featuresActiveIdx === i ? 'w-8 bg-primary' : 'w-1.5 bg-border hover:bg-muted-foreground'}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-12 sm:py-20 bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-xs font-semibold mb-4">Pricing</span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">Simple, Transparent Pricing</h2>
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">Choose the plan that fits your team. Upgrade or downgrade anytime.</p>
            <div className="flex items-center justify-center gap-4 sm:gap-6 mt-4 text-[11px] sm:text-xs text-muted-foreground flex-wrap" data-testid="pricing-trial-badges">
              <span className="flex items-center gap-1.5"><CheckCircle size={14} className="text-success" weight="fill" /> 7-day free trial</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={14} className="text-success" weight="fill" /> Cancel anytime</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {activePlans.map((plan, i) => {
              const Icon = PLAN_ICONS[plan.plan_id] || Buildings;
              const color = PLAN_COLORS[plan.plan_id] || '#7367F0';
              const isPopular = plan.plan_id === 'pro';
              return (
                <div key={plan.plan_id} className={`relative bg-background rounded-xl p-5 sm:p-8 border-2 transition-all ${isPopular ? 'border-primary shadow-xl shadow-primary/10 md:scale-[1.02]' : 'border-border hover:border-primary/30'}`} data-testid={`pricing-card-${plan.plan_id}`}>
                  {isPopular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-bold px-4 py-1 rounded-full">Most Popular</div>}
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}><Icon size={24} style={{ color }} weight="duotone" /></div>
                    <h3 className="text-lg font-bold">{plan.name}</h3>
                  </div>
                  <div className="mb-5">
                    <span className="text-3xl font-bold">${plan.price?.toFixed(0)}</span>
                    <span className="text-sm text-muted-foreground">/user/month</span>
                  </div>
                  <div className="text-sm text-muted-foreground mb-4">
                    {plan.max_projects >= 999999 ? 'Unlimited' : plan.max_projects} Projects, {plan.max_users >= 999999 ? 'Unlimited' : plan.max_users} Users
                  </div>
                  <ul className="space-y-2.5 mb-8">
                    {(plan.features || []).map((f, j) => <li key={j} className="flex items-center gap-2.5 text-sm"><CheckCircle size={16} className="text-emerald-500 shrink-0" weight="fill" />{f}</li>)}
                  </ul>
                  <Button onClick={() => openSignup(plan.plan_id)} className="w-full rounded-md h-11 font-semibold" variant={isPopular ? 'default' : 'outline'} data-testid={`signup-${plan.plan_id}`}>
                    Get Started <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Feature comparison */}
      <section id="compare" className="py-12 sm:py-20 border-t border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-xs font-semibold mb-4">Compare Plans</span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">Features by plan</h2>
            <p className="text-sm text-muted-foreground max-w-xl mx-auto">Every plan includes the core drawing tools. Pro adds advanced measurement, Enterprise unlocks site logistics and cadastre boundaries.</p>
          </div>
          <div className="overflow-x-auto rounded-xl border border-border bg-card" data-testid="feature-compare-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left font-semibold px-5 py-4 w-2/5">Feature</th>
                  <th className="text-center font-semibold px-5 py-4">Basic</th>
                  <th className="text-center font-semibold px-5 py-4 bg-primary/5">Pro</th>
                  <th className="text-center font-semibold px-5 py-4">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { group: 'Drawing & basics', items: [
                    ['Address search & satellite map', true, true, true],
                    ['Markers and text labels', true, true, true],
                    ['Polygon / Rectangle / Circle / Line', true, true, true],
                    ['Arrow annotations', true, true, true],
                    ['Area and distance measurement', true, true, true],
                    ['Snapshot & PDF export', true, true, true],
                  ]},
                  { group: 'Advanced measurement', items: [
                    ['Perimeter tool', false, true, true],
                    ['Multi-distance tool', false, true, true],
                    ['Height estimation (shadow)', false, true, true],
                    ['Angle measurement', false, true, true],
                    ['Historical satellite imagery (Wayback ~194 dates)', false, true, true],
                  ]},
                  { group: 'Project & team', items: [
                    ['Save projects', true, true, true],
                    ['Project sharing with team', false, true, true],
                    ['Team members & roles', '5 users', '25 users', 'Unlimited'],
                    ['Analytics dashboards', false, true, true],
                    ['Audit log', false, true, true],
                    ['Priority email support', false, true, true],
                  ]},
                  { group: 'Site planning (Enterprise)', items: [
                    ['Site logistics markers (Crane, Office, Storage, Toilet, Skip)', false, false, true],
                    ['Cadastre property boundaries (AU)', false, false, true],
                    ['Time Travel — Swipe two dates', false, false, true],
                    ['Time Travel — Timeline scrub & ▶ Play', false, false, true],
                    ['SLA & dedicated support', false, false, true],
                  ]},
                ].map((grp) => (
                  <React.Fragment key={grp.group}>
                    <tr className="bg-secondary/40">
                      <td colSpan={4} className="px-5 py-2 text-[11px] uppercase tracking-wider font-bold opacity-70">{grp.group}</td>
                    </tr>
                    {grp.items.map(([label, b, p, e]) => (
                      <tr key={label} className="border-b border-border last:border-0">
                        <td className="px-5 py-3 text-foreground/85">{label}</td>
                        <td className="px-5 py-3 text-center">{renderCell(b)}</td>
                        <td className="px-5 py-3 text-center bg-primary/5">{renderCell(p)}</td>
                        <td className="px-5 py-3 text-center">{renderCell(e)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-center text-xs text-muted-foreground">All plans include a 7-day free trial. Upgrade or downgrade anytime.</p>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-xs font-semibold mb-4">How It Works</span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">Get Started in 3 Simple Steps</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Sign Up & Create', desc: 'Register your organization, choose a plan, and set up your team in minutes.' },
              { step: '02', title: 'Draw & Measure', desc: 'Search any address, use 12+ drawing tools. Area, distance, angles and height auto-calculate.' },
              { step: '03', title: 'Export & Share', desc: 'Download imagery, share projects with your team, invite users, and generate reports.' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground text-lg font-bold flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary/25">{item.step}</div>
                <h3 className="text-base font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-12 sm:py-20 border-t border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-xs font-semibold mb-4">FAQ</span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">Frequently asked questions</h2>
            <p className="text-sm text-muted-foreground max-w-xl mx-auto">Everything you need to know about RoofMate. Can't find an answer? <a href="#contact" className="text-primary underline">Get in touch</a>.</p>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="landing-faq">
            {LANDING_FAQS.map((item, i) => (
              <AccordionItem key={item.q} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left text-sm sm:text-base font-semibold">{item.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-20 bg-card border-t border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4">Ready to Start Measuring?</h2>
          <p className="text-muted-foreground text-base mb-7 max-w-xl mx-auto">Join hundreds of roofing professionals using RoofMate for satellite measurement, project management, and team collaboration.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => openSignup('basic')} className="rounded-md font-medium text-sm h-10 px-8 shadow-lg shadow-primary/25" data-testid="cta-signup-btn">Create Your Account <ArrowRight size={16} className="ml-1.5" weight="bold" /></Button>
            <Button variant="outline" onClick={() => navigate('/login')} className="rounded-md font-medium text-sm h-10 px-8">Sign In</Button>
          </div>
          <p className="mt-4 text-xs text-muted-foreground" data-testid="cta-trial-note">
            7-day free trial · Cancel anytime
          </p>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-16 sm:py-20 border-t border-border bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-xs font-semibold mb-4">
              <EnvelopeSimple size={14} weight="bold" /> Contact
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">Get in touch with our team</h2>
            <p className="text-muted-foreground text-base max-w-xl mx-auto">
              Questions, partnerships, or custom plans — we usually reply within 1 business day.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Info side */}
            <div className="lg:col-span-2 space-y-5">
              <div className="p-5 rounded-lg border border-border bg-card">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                    <EnvelopeSimple size={20} weight="duotone" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Email us</div>
                    <a href="mailto:info@roofmate.app" className="font-semibold hover:text-primary transition-colors">info@roofmate.app</a>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-3">
                  Send us a note about pricing, demos, integrations, or anything else.
                </p>
              </div>

              <div className="p-5 rounded-lg border border-border bg-card">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Already a customer?</div>
                <p className="text-sm text-muted-foreground mb-3">
                  Existing tenants can open a support ticket directly from inside the app — your AI assistant will try to answer instantly.
                </p>
                <Button variant="outline" size="sm" onClick={() => navigate('/login')} className="rounded-md" data-testid="contact-login-btn">
                  Sign in to support <ArrowRight size={14} className="ml-1" weight="bold" />
                </Button>
              </div>
            </div>

            {/* Form side */}
            <form
              onSubmit={handleContactSubmit}
              className="lg:col-span-3 p-6 sm:p-7 rounded-lg border border-border bg-card space-y-4"
              data-testid="contact-form"
            >
              {contactSuccess ? (
                <div className="py-10 text-center" data-testid="contact-success-state">
                  <div className="w-14 h-14 rounded-full bg-emerald-500/10 text-emerald-400 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={28} weight="fill" />
                  </div>
                  <h3 className="text-lg font-semibold mb-1">Message sent!</h3>
                  <p className="text-sm text-muted-foreground mb-5">
                    Thanks for reaching out. Our team will be in touch within 1 business day.
                  </p>
                  <Button variant="outline" size="sm" onClick={() => setContactSuccess(false)} className="rounded-md" data-testid="contact-send-another-btn">
                    Send another message
                  </Button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="contact-name" className="text-xs">Name *</Label>
                      <Input
                        id="contact-name"
                        value={contactForm.name}
                        onChange={e => setContactForm({ ...contactForm, name: e.target.value })}
                        placeholder="Your name"
                        required
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-email" className="text-xs">Email *</Label>
                      <Input
                        id="contact-email"
                        type="email"
                        value={contactForm.email}
                        onChange={e => setContactForm({ ...contactForm, email: e.target.value })}
                        placeholder="you@company.com"
                        required
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="contact-subject" className="text-xs">Subject</Label>
                    <Input
                      id="contact-subject"
                      value={contactForm.subject}
                      onChange={e => setContactForm({ ...contactForm, subject: e.target.value })}
                      placeholder="How can we help?"
                      data-testid="contact-subject-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-message" className="text-xs">Message *</Label>
                    <textarea
                      id="contact-message"
                      value={contactForm.message}
                      onChange={e => setContactForm({ ...contactForm, message: e.target.value })}
                      placeholder="Tell us a little about what you're looking for…"
                      rows={6}
                      required
                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      data-testid="contact-message-input"
                    />
                  </div>
                  {contactError && (
                    <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2" data-testid="contact-error">
                      {contactError}
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-3 pt-1">
                    <p className="text-xs text-muted-foreground">
                      By submitting, you agree to be contacted by RoofMate.
                    </p>
                    <Button type="submit" disabled={contactLoading} className="rounded-md h-10 px-6" data-testid="contact-submit-btn">
                      {contactLoading ? 'Sending…' : <>Send message <ArrowRight size={14} className="ml-1.5" weight="bold" /></>}
                    </Button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-background py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}><img src="/logo-full.png" alt="RoofMate logo" className="h-7 object-contain" width="150" height="32" loading="lazy" /></div>
          <div className="flex items-center gap-5 text-xs text-muted-foreground">
            <a href="mailto:info@roofmate.app" className="hover:text-foreground transition-colors">info@roofmate.app</a>
            <button onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-foreground transition-colors">Contact</button>
            <span>2026 RoofMate</span>
          </div>
        </div>
      </footer>

      {/* Signup Dialog */}
      <Dialog open={showSignup} onOpenChange={setShowSignup}>
        <DialogContent className="max-w-md" data-testid="signup-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl">Create Your RoofMate Account</DialogTitle>
          </DialogHeader>
          {success ? (
            <div className="py-6 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto">
                <CheckCircle size={32} className="text-emerald-600" weight="fill" />
              </div>
              <div>
                <p className="text-lg font-semibold">Welcome to RoofMate!</p>
                <p className="text-sm text-muted-foreground mt-1">Your account has been created. You can now sign in.</p>
              </div>
              <Button onClick={() => { setShowSignup(false); navigate('/login'); }} className="w-full rounded-md h-10" data-testid="signup-goto-login">
                Go to Sign In <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSignup} className="space-y-4 py-2">
              {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-600 dark:text-red-400">{error}</div>}
              
              {/* Plan selector dropdown */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setPlanMenuOpen(o => !o)}
                  className="w-full flex items-center gap-2 p-3 rounded-lg bg-secondary/50 border border-border hover:bg-secondary transition-colors"
                  data-testid="signup-plan-selector"
                >
                  {React.createElement(PLAN_ICONS[selectedPlan] || Buildings, { size: 18, style: { color: PLAN_COLORS[selectedPlan] || '#7367F0' }, weight: 'duotone' })}
                  <span className="text-sm font-medium capitalize">{selectedPlan} Plan</span>
                  <span className="text-xs text-muted-foreground ml-auto">
                    ${activePlans.find(p => p.plan_id === selectedPlan)?.price?.toFixed(0) || '0'}/mo
                  </span>
                  <CaretDown size={14} weight="bold" className={`text-muted-foreground transition-transform ${planMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {planMenuOpen && (
                  <div className="absolute z-50 left-0 right-0 mt-1 rounded-lg bg-popover border border-border shadow-lg overflow-hidden" data-testid="signup-plan-menu">
                    {activePlans.length === 0 ? (
                      <div className="p-3 text-sm text-muted-foreground">No plans available</div>
                    ) : activePlans.map(plan => {
                      const Icon = PLAN_ICONS[plan.plan_id] || Buildings;
                      const isSelected = plan.plan_id === selectedPlan;
                      return (
                        <button
                          type="button"
                          key={plan.plan_id}
                          onClick={() => { setSelectedPlan(plan.plan_id); setPlanMenuOpen(false); }}
                          className={`w-full flex items-center gap-2 p-3 text-left hover:bg-secondary transition-colors ${isSelected ? 'bg-secondary/70' : ''}`}
                          data-testid={`signup-plan-option-${plan.plan_id}`}
                        >
                          <Icon size={18} style={{ color: PLAN_COLORS[plan.plan_id] || '#7367F0' }} weight="duotone" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium capitalize">{plan.name || `${plan.plan_id} Plan`}</div>
                            {plan.description && (
                              <div className="text-[11px] text-muted-foreground truncate">{plan.description}</div>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">${plan.price?.toFixed(0) || '0'}/mo</span>
                          {isSelected && <Check size={14} weight="bold" className="text-primary" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-[13px] font-medium">Organization Name</Label>
                <Input value={form.organization} onChange={e => setForm(p => ({ ...p, organization: e.target.value }))} placeholder="Your company name" required data-testid="signup-org-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[13px] font-medium">Your Name</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" required data-testid="signup-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[13px] font-medium">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="you@company.com" required data-testid="signup-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[13px] font-medium">Password</Label>
                <div className="relative">
                  <Input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Min 6 characters" required data-testid="signup-password-input" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showPw ? <EyeSlash size={16} /> : <Eye size={16} />}</button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-md h-10 font-semibold" data-testid="signup-submit-btn">
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                Already have an account? <button type="button" onClick={() => { setShowSignup(false); navigate('/login'); }} className="text-primary hover:underline font-medium">Sign In</button>
              </p>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Floating "back to top" button */}
      <button
        type="button"
        onClick={scrollToTop}
        aria-label="Scroll back to top"
        data-testid="scroll-to-top-btn"
        className={`fixed bottom-6 right-6 z-50 h-11 w-11 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
          showTopBtn ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-3 pointer-events-none'
        }`}
      >
        <ArrowUp size={20} weight="bold" />
      </button>

      {/* Image Lightbox */}
      {lightboxImg && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={lightboxImg.alt}
          onClick={() => setLightboxImg(null)}
          data-testid="showcase-lightbox"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-8 animate-fadeIn cursor-zoom-out"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxImg(null); }}
            aria-label="Close enlarged image"
            data-testid="showcase-lightbox-close"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <X size={20} weight="bold" />
          </button>
          <img
            src={lightboxImg.src}
            alt={lightboxImg.alt}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[95vw] w-auto h-auto rounded-lg shadow-2xl object-contain cursor-default"
            data-testid="showcase-lightbox-img"
          />
        </div>
      )}
      {/* Time Travel — public demo modal */}
      <TimeTravelDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} onSignup={() => openSignup('enterprise')} />
    </div>
  );
}

// -- Time Travel public demo --------------------------------------------------
// Plays a server-built 6-frame GIF time-lapse (Esri Wayback over Sydney CBD)
// so unauthenticated visitors get to feel the feature before signing up.
// The asset itself is animated, so we just need a styled frame + CTA.
function TimeTravelDemoModal({ open, onClose, onSignup }) {
  const [loaded, setLoaded] = React.useState(false);
  const [bustKey, setBustKey] = React.useState(0);
  React.useEffect(() => {
    if (!open) return;
    setLoaded(false);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);
  if (!open) return null;
  // The GIF loops natively at ~800ms/frame — "Replay" simply re-requests it
  // (cache-busted) so the user can re-watch from frame 0 on demand.
  const apiBase = process.env.REACT_APP_BACKEND_URL || '';
  const gifSrc = `${apiBase}/api/wayback/demo${bustKey ? `?r=${bustKey}` : ''}`;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Time Travel — see it in action"
      onClick={onClose}
      data-testid="time-travel-demo-modal"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-8 animate-fadeIn"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl bg-card rounded-2xl border border-border shadow-2xl overflow-hidden"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close demo"
          data-testid="time-travel-demo-close"
          className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur transition-colors"
        >
          <X size={18} weight="bold" />
        </button>
        <div className="relative bg-black aspect-[8/5] flex items-center justify-center">
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center text-white/80 text-sm" data-testid="time-travel-demo-loading">
              <span className="w-5 h-5 mr-2 rounded-full border-2 border-white/30 border-t-white animate-spin" /> Loading demo…
            </div>
          )}
          <img
            key={bustKey}
            src={gifSrc}
            alt="A 6-frame animated time-lapse over Herbert Street, Slacks Creek QLD generated by RoofMate Time Travel"
            onLoad={() => setLoaded(true)}
            onError={() => setLoaded(true)}
            className={`w-full h-full object-cover transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
            data-testid="time-travel-demo-gif"
          />
        </div>
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-3 mb-3">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-bold uppercase tracking-wide shrink-0">
              <ClockCounterClockwise size={12} weight="duotone" /> Time Travel demo
            </span>
            <span className="text-xs text-muted-foreground pt-1">Slacks Creek QLD · 6 frames · 2014 → today</span>
          </div>
          <p className="text-sm text-foreground/90 leading-relaxed mb-4">
            This is the same Esri World Imagery Wayback engine that powers Time Travel inside RoofMate.
            On your own projects you pick the two dates, swipe between them or auto-play the Timeline,
            then export the result as a branded GIF for clients.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => { onClose(); onSignup?.(); }}
              className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-95 transition-opacity"
              data-testid="time-travel-demo-signup-cta"
            >
              Try it on your property →
            </button>
            <button
              type="button"
              onClick={() => { setLoaded(false); setBustKey(Date.now()); }}
              className="px-4 py-2 rounded-md bg-secondary/70 hover:bg-secondary text-sm font-semibold transition-colors"
              data-testid="time-travel-demo-replay"
            >
              ↺ Replay
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
